export enum TaskStatus {
  QUEUED = "queued",
  IN_PROGRESS = "in_progress",
  READY_FOR_REVIEW = "ready_for_review",
  COMPLETED = "completed",
  FAILED = "failed",
  DISMISSED = "dismissed"
}

export interface Task {
  id: number;
  ai_team_member: string;
  feature_id?: string;
  title: string;
  description: string;
  status: TaskStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
  claude_output?: string;
  result?: string;
  error?: string;
  is_quicktask?: boolean;
  claude_session_id?: string;  // JSONL session ID for this task
  model?: string;  // Claude model used for this task
}

export interface Message {
  id: number;
  task_id: number;
  sender: 'human' | 'ai';
  content: string;
  created_at: string;
  claude_output?: string;
}

export interface AITeamMember {
  name: string;
  role: string;
  file_path: string;
  queued_tasks: number;
  in_progress_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
}

export interface HumanTeamMember {
  name: string;
  queued_tasks: number;
  in_progress_tasks: number;
  completed_tasks: number;
  failed_tasks: number;
}

export interface TaskCreate {
  ai_team_member: string;
  feature_id?: string;
  title: string;
  description: string;
  created_by: string;
  is_quicktask?: boolean;
}

// Feature Management Types
export interface FeatureCategory {
  id: string;
  name: string;
  order: number;
}

export enum FeaturePriority {
  CORE = "core",
  DECEMBER = "december",
  JANUARY = "january",
  FEBRUARY = "february",
  MARCH = "march",
  UNSCHEDULED = "unscheduled",
  DEPRIORITIZED = "deprioritized"
}

export enum FeatureStatus {
  NOT_STARTED = "not_started",
  IN_PROGRESS = "in_progress",
  IN_REVIEW = "in_review",
  DONE = "done"
}

export interface Feature {
  id: string;
  name: string;
  category: string;
  expectations: string;
  order: number;
  owner?: string;
  priority?: string;  // FeaturePriority value
  status?: string;  // Stored status (mainly for "done")
  computed_status?: string;  // FeatureStatus value computed from tasks
  created_at: string;
  updated_at: string;
  changes?: string;
  inputs?: string;
  prompt?: string;
}

export interface FeaturesByCategory {
  categories: FeatureCategory[];
  features: { [categoryId: string]: Feature[] };
  priority_levels?: string[];
  status_values?: string[];
}

export interface MessageCreate {
  task_id: number;
  sender: 'human' | 'ai';
  content: string;
  claude_output?: string;
  model?: string;  // Claude model to use for this message
}

// Ideas Types
export enum IdeaType {
  NEW_FEATURE = "new_feature",
  FEATURE_ENHANCEMENT = "feature_enhancement"
}

export enum IdeaStatus {
  PROPOSED = "proposed",
  ACCEPTED = "accepted",
  REJECTED = "rejected",
  DEFERRED = "deferred"
}

export interface Idea {
  id: number;
  title: string;
  description: string;
  idea_type: IdeaType;
  status: IdeaStatus;
  category: string;
  feature_id?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  ai_team_member?: string;
  created_feature_id?: string;
  created_task_id?: number;
}

export interface IdeaCreate {
  title: string;
  description: string;
  idea_type: IdeaType;
  category: string;
  feature_id?: string;
  created_by: string;
  ai_team_member?: string;
}

export interface IdeasByCategory {
  categories: FeatureCategory[];
  ideas: { [categoryId: string]: Idea[] };
}

// JSONL Session Monitoring Types
// These represent the rich data extracted from Claude Code's JSONL files

export interface JsonlToolCall {
  id: string;
  name: string;
  input: Record<string, any>;
}

export interface JsonlToolResult {
  tool_use_id: string;
  content: string;
}

export interface JsonlTodo {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  activeForm?: string;
}

export interface JsonlMessage {
  uuid: string;
  type: 'user' | 'assistant';
  timestamp: string;
  session_id: string;
  text?: string;
  thinking?: string;
  has_thinking?: boolean;
  redacted_thinking_count?: number;
  has_redacted_thinking?: boolean;
  tool_calls?: JsonlToolCall[];
  tool_results?: JsonlToolResult[];
  todos?: JsonlTodo[];
}

export interface JsonlUpdate {
  type: 'message' | 'status' | 'session_file' | 'session_detected' | 'warning' | 'error';
  data?: JsonlMessage;
  message?: string;
  file?: string;
  session_id?: string;
}

export interface ActiveSession {
  active: boolean;
  session_id?: string;
  file?: string;
  last_modified?: number;
  size_bytes?: number;
  message?: string;
}

// ============ RELEASES ============

export enum ReleaseStatus {
  GENERATING_NOTES = "generating_notes",
  NOTES_READY = "notes_ready",
  AWAITING_TASKS = "awaiting_tasks",
  RE_REVIEWING = "re_reviewing",
  REVIEW_REJECTED = "review_rejected",
  RELEASING = "releasing",
  RELEASED = "released",
  FAILED = "failed",
  CANCELED = "canceled",
}

export interface ReleaseSuggestion {
  text: string;
  task_id?: number | null;
  rejected?: boolean;
  area?: string | null;  // repo key / area the finding belongs to (grouping)
}

export type ReleaseFixMode = 'batched' | 'per_suggestion';

export interface Release {
  id: number;
  title: string;
  status: ReleaseStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at?: string | null;
  release_notes?: string | null;
  notes_output?: string | null;
  notes_session_id?: string | null;
  suggestions: ReleaseSuggestion[];
  review_output?: string | null;
  review_session_id?: string | null;
  review_passed?: boolean | null;
  release_output?: string | null;
  release_session_id?: string | null;
  error?: string | null;
  auto_loop_active?: boolean;
  auto_loop_iterations?: number;
  instructions?: string | null;
  model?: string | null;       // release's own steps (notes, review, release)
  fix_model?: string | null;   // fix tasks spawned from suggestions
  fix_mode?: ReleaseFixMode;
}
